/**
 * Press feedback — two helpers for the two press tiers.
 *
 *  - `<Pressable tier="surface">` wraps a tappable CARD/surface in a gated
 *    `<motion.*>` (whileTap scale). Use for non-form, non-submit surfaces.
 *  - `pressClass(tier)` returns a CSS-only press class for REAL
 *    `<button type="submit">` / shadcn `<Button>` / `<a>` elements, so we never
 *    wrap a form-submit / RHF-bound control in `motion()` (which could reparent
 *    it out of the form). It gates via Tailwind's `motion-reduce:` variant.
 *
 * No ripple, no elevation, no bounce — just a brief scale.
 */
import { motion } from "framer-motion";
import type { ReactNode } from "react";

import { MOTION_TAGS, type MotionWrapperProps } from "@/components/motion/shared";
import { useGardenMotion } from "@/lib/use-garden-motion";

export type PressTier = "surface" | "cta" | "navTab";

/** CSS-only press feedback for native buttons/links/shadcn Buttons. */
export function pressClass(tier: PressTier = "cta"): string {
  const scale =
    tier === "navTab"
      ? "active:scale-[0.92]"
      : tier === "surface"
        ? "active:scale-[0.98]"
        : "active:scale-95";
  return `transition-transform ${scale} motion-reduce:transition-none motion-reduce:active:scale-100`;
}

export interface PressableProps extends MotionWrapperProps {
  tier?: PressTier;
  onClick?: () => void;
}

export function Pressable({
  as = "div",
  tier = "surface",
  className,
  children,
  onClick,
  "data-testid": testId,
}: PressableProps): ReactNode {
  const { press } = useGardenMotion();
  const Comp = MOTION_TAGS[as] as typeof motion.div;
  return (
    <Comp
      className={className}
      data-testid={testId}
      onClick={onClick}
      {...press(tier)}
    >
      {children}
    </Comp>
  );
}
